/**
 * The facts both documents quote, in one place.
 *
 * Policy prose goes stale the moment a name, an address or a storage key is
 * written twice, so every one of them is stated here and nowhere else.
 */

export type LegalDoc = 'privacy' | 'terms';

export const APP = 'Waning';
export const SITE = 'waning.app';
export const DEVELOPER = 'Bangonkali';
export const CONTACT = 'support@waning.app';

/** Shown at the head of both documents; bump it when the wording changes. */
export const UPDATED = '15 September 2026';

/* Literal, not `Record<LegalDoc, string>`: these are route paths, and the
   router only type-checks a link whose destination it can read exactly. */
export const PATHS = {
  privacy: '/privacy-policy',
  terms: '/terms-of-service',
} as const satisfies Record<LegalDoc, string>;

/**
 * Prose is read, not driven: pinch zoom comes back for the documents.
 *
 * It lives here rather than beside the page component because a route's `head`
 * is not code-split — reading it from `Page.tsx` would put the whole document
 * shell in the chunk the deck loads.
 */
export const DOC_VIEWPORT = 'width=device-width, initial-scale=1, viewport-fit=cover';

/** The storage keys the privacy policy describes, as `src/config/storage.ts` writes them. */
export const KEYS = {
  prefs: 'turntable.prefs.v1',
  creds: 'turntable.creds.v1',
} as const;

/** The scope the app asks Google for, from `src/config/defaults.ts`. */
export const SCOPE = 'https://www.googleapis.com/auth/youtube.readonly';

export const LINKS = {
  googlePrivacy: 'https://policies.google.com/privacy',
  youtubeTerms: 'https://www.youtube.com/t/terms',
  apiTerms: 'https://developers.google.com/youtube/terms/api-services-terms-of-service',
  permissions: 'https://myaccount.google.com/permissions',
  cloudflarePrivacy: 'https://www.cloudflare.com/privacypolicy/',
} as const;

export interface DocMeta {
  title: string;
  description: string;
}

export const DOCS: Record<LegalDoc, DocMeta> = {
  privacy: {
    title: `Privacy Policy — ${APP}`,
    description: `How ${APP} handles your data: it has no server, no account and no analytics, and nothing it remembers leaves your browser.`,
  },
  terms: {
    title: `Terms of Service — ${APP}`,
    description: `The terms for using ${APP}, a free client-side turntable that plays music through YouTube's embedded player.`,
  },
};
